import { useHistory, useRouteMatch } from 'react-router-dom';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isNil } from 'lodash/fp';
import { getUrlParams, setUrlParams } from '../../../../utils/helpers/urlParams';
import { ITEMS_PER_PAGE } from '../constants';

type ParamType = false | string | null | number | string;

const useSearchParams = () => {
    const history = useHistory();
    const { isExact } = useRouteMatch();
    const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
    const [page, setPage] = useState(0);

    const handleUrlParamSet = <T extends ParamType>(setState: Dispatch<SetStateAction<T>>) =>
        (paramObj: Record<string, ParamType>) => {
            if (isExact) {
                const paramName = Object.keys(paramObj)[0];
                const param = paramObj[paramName];
                setState(param as T);

                const newUrlParams = setUrlParams(history.location.search, {
                    [paramName]: isNil(param) ? '' : param || '',
                });

                history.push({ search: newUrlParams.toString() });
            }
        };

    useEffect(() => {
        if (isExact) {
            const [
                rowsPerPageParam,
                pageParam,
            ] = getUrlParams({
                searchString: history.location.search,
                params: ['rowsPerPage', 'page']
            });

            setRowsPerPage(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : ITEMS_PER_PAGE);
            setPage(pageParam ? parseInt(pageParam, 10) : 0);

            const newUrlParams = setUrlParams(history.location.search, {
                rowsPerPage: rowsPerPageParam || ITEMS_PER_PAGE,
                page: pageParam || 0,
            });

            history.push({ search: newUrlParams.toString() });
        }
    // eslint-disable-next-line
    }, [isExact]);

    const handleSetRowsPerPage = (p: ParamType) => handleUrlParamSet(setRowsPerPage)({ rowsPerPage: p });
    const handleSetPage = (r: ParamType) => handleUrlParamSet(setPage)({ page: r });

    return {
        rowsPerPage, setRowsPerPage: handleSetRowsPerPage,
        page, setPage: handleSetPage,
    };
};

export default useSearchParams;
